import React, { useEffect } from 'react'
import { Provider } from 'react-redux'
import { Box } from '@chakra-ui/react'
import { Poppins } from 'next/font/google'
import Head from 'next/head'
import Script from 'next/script'

import { NavigationClient } from 'components/nav/NavigationClient'
import { Footer, Newsletter } from 'components/partials/shared'
import AuthProvider from 'components/partials/shared/AuthProvider/index.'
import { Snackbar } from 'components/ui/Snackbar'
import AppWrapper from 'contexts/AppWrapper'
import CustomerInfoProvider from 'contexts/CustomerInfoProvider'
import { store } from 'store'
import { STORAGE_KEYS } from 'utils/constants'
import i18n from 'utils/i18n'
import { IntercomProvider } from 'utils/intercom/IntercomProvider'
import { getFromStorage } from 'utils/storage'

import 'config/wdyr'
import 'react-quill/dist/quill.snow.css'
import 'utils/i18n'
import '../styles/globalStyles.css'

import { Provider as ChakraUIProvider } from '../components/ui/provider'

const poppins = Poppins({
  weight: ['100', '200', '300', '400', '500', '600', '700', '900'],
  subsets: ['latin'],
})

type Props = {
  Component: any
  pageProps: any
}

const MyApp: React.FC<Props> = ({ Component, pageProps }) => {
  useEffect(() => {
    const lng = JSON.parse(getFromStorage(STORAGE_KEYS.language))

    if (lng) {
      i18n.changeLanguage(lng)
    }
  }, [])

  return (
    <main className={poppins.className}>
      {/* <GlobalStyles /> */}
      <Head>
        <title>Leid</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>

      <AuthProvider>
        <CustomerInfoProvider>
          <Provider store={store}>
            <AppWrapper>
              <ChakraUIProvider>
                <IntercomProvider>
                  <NavigationClient />
                  <Snackbar />

                  <Box minHeight="100vh" display="flex" flexDirection="column" paddingTop="76px">
                    <Box flexGrow={1}>
                      {/* Google Tag Manager - Global base code */}
                      <Script
                        id="gtm-manager"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                          __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer', '${process.env.NEXT_PUBLIC_GTM_ID}');
              `,
                        }}
                      />
                      <Component {...pageProps} />

                      <Script
                        id="CookieConsent"
                        strategy="afterInteractive"
                        src="https://policy.app.cookieinformation.com/uc.js"
                        data-culture="EN"
                        type="text/javascript"
                      />
                      <Script
                        async
                        type="text/javascript"
                        src="https://static.klaviyo.com/onsite/js/Vih5FQ/klaviyo.js"
                        strategy="afterInteractive"
                      />
                      <Script
                        id="klaviyo-script"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                          __html: `
                !function(){if(!window.klaviyo){window._klOnsite=window._klOnsite||[];try{window.klaviyo=new Proxy({},{get:function(n,i){return"push"===i?function(){var n;(n=window._klOnsite).push.apply(n,arguments)}:function(){for(var n=arguments.length,o=new Array(n),w=0;w<n;w++)o[w]=arguments[w];var t="function"==typeof o[o.length-1]?o.pop():void 0,e=new Promise((function(n){window._klOnsite.push([i].concat(o,[function(i){t&&t(i),n(i)}]))}));return e}}})}catch(n){window.klaviyo=window.klaviyo||[],window.klaviyo.push=function(){var n;(n=window._klOnsite).push.apply(n,arguments)}}}}();
              `,
                        }}
                      />

                      <Script
                        id="delta-script"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                          __html: `
                var _d7=_d7||[];
                _d7.push({
                  action:"pageView",
                  pageId:"${process.env.NEXT_DELTA_PAGE_ID}"
                });
                (function(){
                  var d=document.createElement("script"),s=document.getElementsByTagName("script")[0];
                  _d7.id="18689";_d7.p=("https:" == document.location.protocol ? "https://" : "http://");
                  d.src=_d7.p+"tb.de17a.com/d7.js";d.type="text/javascript";d.async=1;s.parentNode.insertBefore(d,s);
                })();
              `,
                        }}
                      />
                    </Box>

                    <>
                      <Newsletter />
                      <Footer />
                    </>
                  </Box>
                </IntercomProvider>
              </ChakraUIProvider>
            </AppWrapper>
          </Provider>
        </CustomerInfoProvider>
      </AuthProvider>
    </main>
  )
}

export default MyApp
